import { StarScore, level, useStarColors } from '../components/five-star/stars';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { matchesGroup, inPeriodOne, PERIOD_LABELS } from '../components/dashboard/PermitWidgets';
import { useTcSearchIds } from '../lib/tcPrivacy';
import { PermitHowtoDialog } from '../components/work-permits/howto/PermitHowtoDialog';
import { DEFICIENT_STEPS, DEFICIENT_SECTIONS } from '../components/work-permits/howto/deficientSteps';
import { KvkkRenewalBanner } from '../components/firm/KvkkRenewalBanner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { storedIdemKey, clearIdemKey, idemConfig, handleIdemConflict } from '../lib/idempotency';
import { StatusMultiFilter, usePersistedStatuses } from '../components/work-permits/StatusDefaultToggle';
import { useWorkPermits } from '../hooks/useWorkPermits';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { ActionTip } from '../components/work-permits/ActionTip';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'sonner';
import { writeStyledExcel } from '../lib/styledExcel';
import { PermitQrDialog } from '../components/work-permits/PermitQrDialog';
import { MASK_STEPS, MASK_SECTIONS } from '../components/work-permits/howto/maskSteps';
import { MASK_HOWTO_KEY } from '../lib/healthMask';
import { QrCode, Unlock, Ban, Plus, Pencil, Trash2, ClipboardCheck, Eye, EyeOff, CheckCircle, AlertCircle, Loader2, Search, Users, Download, Truck, Wrench, Briefcase, Check, X, Clock, XCircle, FileText, ArrowRight, Undo2, Copy } from 'lucide-react';
import { TablePagination } from '../components/TablePagination';

// Modüler bileşenler
import EmployeeSelector from '../components/work-permits/EmployeeSelector';
import EquipmentSelector from '../components/work-permits/EquipmentSelector';
import WorkPermitWizard from '../components/work-permits/WorkPermitWizard';
import WorkPermitDetailDialog from '../components/work-permits/WorkPermitDetailDialog';
import { PermitsCalendar } from '../components/work-permits/PermitsCalendar';
import { List, CalendarDays, AlertTriangle, BookOpen, HelpCircle } from 'lucide-react';
import { DocRulesDialog } from '../components/work-permits/DocRulesDialog';
import { 
  WORK_PERMIT_TYPES, 
  formatDate, 
  maskTcNo, 
  getTodayString, 
  isCompanyFullyApproved, 
  isEmployeeFullyApproved,
  getActualStatus,
  getStatusLabel,
  getEquipmentValidityInfo
} from '../components/work-permits/constants';
import { confirmDialog, promptDialog } from '../components/ConfirmHost';
import { useOnline, cacheApprovedPermits } from '../lib/offline';
import { fetchAllPages } from '../hooks/useWorkPermits';
import { OfflinePermitsView } from '../components/work-permits/OfflinePermitsView';
import { useScreenReady } from '../lib/perfScreen';

const WorkPermitsPage = () => {
  const navigate = useNavigate();
  const { user, hasLocationAccess, canEdit, isFirm, isReviewer } = useAuth();
  const canDeletePermit = (p) => p.status === 'taslak' && p.created_by === user?.id;
  const { myCompany, reloadMyCompany } = useOutletContext() || {};
  const kvkkLocked = !!myCompany?.kvkk_renewal_required;
  
  // Custom hook ile data ve actions
  const {
    permits, companies, locations, allEmployees, allEquipment, authorizedLocations, loading,
    selectedLocationFilter, setSelectedLocationFilter,
    selectedCompanyFilter, setSelectedCompanyFilter,
    searchQuery, setSearchQuery,
    companySearchQuery, setCompanySearchQuery,
    fetchData, fetchPermits, fetchEmployeesForCompany, refreshEquipment, createPermit, updatePermit, deletePermit,
    getCompanyName, getLocationName, getEmployeeById, getCompaniesForLocation, getEquipmentForCompany
  } = useWorkPermits(hasLocationAccess, isFirm ? 'all' : 'isg_inceleme');
  useScreenReady('izin_listesi_donus', !loading);
  const [statusSel, setStatusFilter, saveStatusSel] = usePersistedStatuses(isFirm ? [] : ['isg_inceleme'], !!(window.history.state?.usr?.dashGroup || window.history.state?.usr?.openPermitId));

  // Çevrimdışı mod (admin / İSG): onaylı talepler cihaza kaydedilir, bağlantı gelince yenilenir
  const online = useOnline();
  const [wasOffline, setWasOffline] = useState(false);
  useEffect(() => {
    if (!online) { setWasOffline(true); return; }
    if (wasOffline) { setWasOffline(false); fetchData(); fetchPermits(); }
  }, [online]);
  // Çevrimdışı önbellek: onaylı izinlerin TAM kayıtları (liste yanıtı hafif olduğu için ayrıca alınır)
  const [fullActive, setFullActive] = useState(null);
  const cacheAllowed = !isFirm && !loading && online && user?.id && selectedLocationFilter === 'all' && selectedCompanyFilter === 'all';
  useEffect(() => {
    if (!cacheAllowed) return;
    const isActive = p => getActualStatus(p) === 'aktif' && hasLocationAccess(p.location_id);
    const ids = new Set(permits.filter(isActive).map(p => p.id));
    if (!ids.size) { setFullActive([]); return; }
    let cancelled = false;
    (async () => {
      const list = (await fetchAllPages('/work-permits?status=aktif')).filter(p => ids.has(p.id));
      const missing = [...ids].filter(id => !list.some(p => p.id === id));
      const extra = await Promise.all(missing.map(id => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/work-permits/${id}`).then(r => r.data).catch(() => null)));
      if (!cancelled) setFullActive([...list, ...extra.filter(Boolean)]);
    })().catch(() => {});
    return () => { cancelled = true; };
  }, [permits, cacheAllowed]);
  useEffect(() => {
    if (!cacheAllowed || !fullActive) return;
    cacheApprovedPermits(user.id, fullActive, { companies, locations, allEmployees, allEquipment }, p => getActualStatus(p) === 'aktif' && hasLocationAccess(p.location_id));
  }, [fullActive, companies, locations, allEmployees, allEquipment]);

  // Local state
  // Göz tercihleri tarayıcıda saklanır
  const [showAllEmployees, setShowAllEmployees] = useState(() => localStorage.getItem('permits-show-employees') === '1');
  const [showAllEquipment, setShowAllEquipment] = useState(() => localStorage.getItem('permits-show-equipment') === '1');
  useEffect(() => { localStorage.setItem('permits-show-employees', showAllEmployees ? '1' : '0'); }, [showAllEmployees]);
  useEffect(() => { localStorage.setItem('permits-show-equipment', showAllEquipment ? '1' : '0'); }, [showAllEquipment]);
  const [employees, setEmployees] = useState([]);
  const [companyEquipment, setCompanyEquipment] = useState([]);
  const [hasEquipment, setHasEquipment] = useState(false);
  const [hasWorkPermitTypes, setHasWorkPermitTypes] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingPermit, setEditingPermit] = useState(null);
  const [viewingPermit, setViewingPermit] = useState(null);
  const canCalendar = !isFirm && isReviewer;
  const [pageView, setPageViewState] = useState(() => localStorage.getItem('work-permits-view') || 'list');
  const setPageView = (v) => { localStorage.setItem('work-permits-view', v); setPageViewState(v); };
  const calendarMode = canCalendar && pageView === 'calendar';
  const [docRulesOpen, setDocRulesOpen] = useState(false);
  const [requestNumberSearch, setRequestNumberSearch] = useState(''); // Talep No arama
  // Dashboard grafiğinden gelen durum + dönem filtresi
  const routerLocation = useLocation();
  const [dashFilter, setDashFilter] = useState(() => routerLocation.state?.dashGroup ? { group: routerLocation.state.dashGroup, period: routerLocation.state.dashPeriod || 'all' } : null);
  const tcSearchIds = useTcSearchIds(searchQuery);
  // Talep oluşturma gösterimi: firmaya ilk girişte bir kez otomatik açılır
  // İlk kez talep oluşturacak firmaya (hiç talebi yoksa) bir kez otomatik açılır; izlenene kadar düğme parlar
  const howtoKey = `permit-howto-seen-${user?.id}`;
  const [howtoOpen, setHowtoOpen] = useState(false);
  const [howtoPulse, setHowtoPulse] = useState(() => isFirm && !localStorage.getItem(howtoKey));
  const openHowto = () => { localStorage.setItem(howtoKey, '1'); setHowtoPulse(false); setHowtoOpen(true); };
  useEffect(() => {
    if (isFirm && user?.id && !loading && permits.length === 0 && !localStorage.getItem(howtoKey)) openHowto();
  }, [isFirm, user?.id, loading, permits.length]);
  // Eksik/Hatalı gösterimi: firmanın ilk eksik/hatalı talebinde bir kez otomatik açılır; izlenene kadar düğme parlar
  const defKey = `permit-deficient-seen-${user?.id}`;
  const hasDeficient = permits.some(p => p.status === 'eksik_hatali');
  const [defOpen, setDefOpen] = useState(false);
  const [maskOpen, setMaskOpen] = useState(false);
  const copyBusy = useRef(new Set());
  const [howtoHidden, setHowtoHidden] = useState(() => localStorage.getItem('permit-howto-hidden') === '1');
  const toggleHowtoHidden = () => setHowtoHidden(h => { localStorage.setItem('permit-howto-hidden', h ? '0' : '1'); return !h; });
  const [maskPulse, setMaskPulse] = useState(() => isFirm && !localStorage.getItem(MASK_HOWTO_KEY));
  const openMaskHowto = () => { localStorage.setItem(MASK_HOWTO_KEY, '1'); setMaskPulse(false); setMaskOpen(true); };
  const [defSeen, setDefSeen] = useState(() => !!localStorage.getItem(defKey));
  const defPulse = isFirm && hasDeficient && !defSeen;
  const openDefHowto = () => { localStorage.setItem(defKey, '1'); setDefSeen(true); setDefOpen(true); };
  useEffect(() => { if (isFirm && user?.id && !loading && hasDeficient && !localStorage.getItem(defKey)) openDefHowto(); }, [isFirm, user?.id, loading, hasDeficient]);
  const [pendingOpenId, setPendingOpenId] = useState(() => routerLocation.state?.openPermitId || null);
  useEffect(() => {
    if (!dashFilter && !pendingOpenId) return;
    if (dashFilter) setStatusFilter('all');
    setPageView('list');
    navigate(routerLocation.pathname, { replace: true, state: null });
  }, []);
  
  // Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editWizardOpen, setEditWizardOpen] = useState(false);
  const [editWizardPermit, setEditWizardPermit] = useState(null);
  
  // 2. Onay state'leri
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingPermit, setRejectingPermit] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  
  // Work Areas state
  const [workAreas, setWorkAreas] = useState([]);
  const [availableIsTipleri, setAvailableIsTipleri] = useState([]);
  const [availableIsCesitleri, setAvailableIsCesitleri] = useState([]);
  const [selectedWorkArea, setSelectedWorkArea] = useState(null);
  const [enabledOptionalDocs, setEnabledOptionalDocs] = useState([]); // Opsiyonel belgelerden zorunlu yapılanlar
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  const [formData, setFormData] = useState({ 
    location_id: '', company_id: '', job_description: '',
    start_date: '', end_date: '', start_time: '', end_time: '',
    employee_ids: [], equipment_ids: [], work_permit_types: [],
    is_tipi: '', is_cesidi: '', work_area_id: ''
  });

  const [starScores, setStarScores] = useState({});
  const [lowScore, setLowScore] = useState(2.5);
  const [empScores, setEmpScores] = useState({});
  const starColors = useStarColors();
  const starOf = (p) => starScores[p.behalf_company_id || p.company_id];
  const isLow = (p) => (starOf(p)?.avg ?? 99) < lowScore;
  const isLowNew = (p) => !isFirm && isLow(p) && ['isg_inceleme', 'onay_bekliyor'].includes(p.status);
  useEffect(() => { if (!isFirm && user?.id) axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/five-star/company-scores`).then(r => { setStarScores(r.data.scores); setLowScore(r.data.threshold); setEmpScores(r.data.employees || {}); }).catch(() => setStarScores({})); }, [isFirm, user?.id]);

  // Filter permits
  const filteredPermits = permits.filter(permit => {
    if (!hasLocationAccess(permit.location_id)) return false;
    if (selectedLocationFilter !== 'all' && permit.location_id !== selectedLocationFilter) return false;
    if (selectedCompanyFilter !== 'all' && permit.company_id !== selectedCompanyFilter) return false;
    
    if (dashFilter && (!matchesGroup(dashFilter.group, permit) || !inPeriodOne(permit, dashFilter.period))) return false;
    const actualStatus = getActualStatus(permit);
    if (statusSel.length && !statusSel.some(k => (k === 'magaza_itirazi' ? permit.store_objection : k === 'dusuk_puan' ? isLowNew(permit) : actualStatus === k))) return false;
    
    // Talep No araması
    if (requestNumberSearch.length >= 1) {
      const reqNum = (permit.request_number || '').toLowerCase();
      if (!reqNum.includes(requestNumberSearch.toLowerCase())) return false;
    }
    
    if (companySearchQuery.length >= 3) {
      const companyName = getCompanyName(permit.company_id).toLowerCase();
      if (!companyName.includes(companySearchQuery.toLowerCase())) return false;
    }
    
    if (searchQuery.length >= 3) {
      const permitEmployees = (permit.employee_ids || []).map(id => getEmployeeById(id)).filter(Boolean);
      const hasMatch = permitEmployees.some(emp => {
        const fullName = `${emp.name} ${emp.surname}`.toLowerCase();
        return fullName.includes(searchQuery.toLowerCase()) || !!tcSearchIds?.has(emp.id);
      });
      if (!hasMatch) return false;
    }
    return true;
  });

  // Pagination için slice
  const totalItems = filteredPermits.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const paginatedPermits = filteredPermits.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Sayfa değiştiğinde
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Sayfa boyutu değiştiğinde
  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  // Filter değiştiğinde sayfayı sıfırla
  useEffect(() => {
    setCurrentPage(1);
  }, [statusSel, selectedLocationFilter, selectedCompanyFilter, searchQuery, companySearchQuery, dashFilter]);

  // Work Areas fonksiyonları
  const fetchWorkAreas = async (locationId) => {
    if (!locationId) {
      setWorkAreas([]);
      setAvailableIsTipleri([]);
      return;
    }
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/work-areas?location_id=${locationId}`);
      setWorkAreas(response.data.filter(a => a.is_active !== false));
      // Benzersiz iş tiplerini çıkar
      const isTipleri = [...new Set(response.data.filter(a => a.is_active !== false).map(a => a.is_tipi))];
      setAvailableIsTipleri(isTipleri.sort());
    } catch (error) {
      console.error('Work areas fetch error:', error);
    }
  };

  const handleIsTipiChange = (isTipi) => {
    setFormData({ ...formData, is_tipi: isTipi, is_cesidi: '', work_area_id: '', employee_ids: [] });
    setSelectedWorkArea(null);
    setEnabledOptionalDocs([]);
    // İş tipine göre iş çeşitlerini filtrele
    const cesitler = workAreas
      .filter(a => a.is_tipi === isTipi)
      .map(a => ({ id: a.id, is_cesidi: a.is_cesidi }));
    setAvailableIsCesitleri(cesitler);
  };

  const handleIsCesidiChange = (isCesidi) => {
    const area = workAreas.find(a => a.is_tipi === formData.is_tipi && a.is_cesidi === isCesidi);
    setFormData({ ...formData, is_cesidi: isCesidi, work_area_id: area?.id || '', employee_ids: [] });
    setSelectedWorkArea(area || null);
    setEnabledOptionalDocs([]);
  };

  // Opsiyonel belgeyi zorunlu yap/kaldır
  const toggleOptionalDoc = (docKey) => {
    setEnabledOptionalDocs(prev => 
      prev.includes(docKey) ? prev.filter(k => k !== docKey) : [...prev, docKey]
    );
    // Personel listesini sıfırla çünkü filtre değişti
    setFormData({ ...formData, employee_ids: [] });
  };

  // Seçilen work area'ya göre firma/personel uygunluğunu kontrol et
  const isCompanyCompliantForArea = (company, workArea) => {
    if (!workArea || !workArea.required_company_docs || workArea.required_company_docs.length === 0) return true;
    const docs = company?.documents || {};
    return workArea.required_company_docs.every(docKey => docs[docKey] === 'onay');
  };

  const isEmployeeCompliantForArea = (employee, workArea) => {
    if (!workArea) return true;
    const docs = employee?.documents || {};
    
    // Zorunlu belgeler
    const requiredDocs = workArea.required_employee_docs || [];
    const hasAllRequired = requiredDocs.every(docKey => docs[docKey] === 'onay');
    
    // Aktifleştirilmiş opsiyonel belgeler
    const hasAllEnabledOptional = enabledOptionalDocs.every(docKey => docs[docKey] === 'onay');
    
    return hasAllRequired && hasAllEnabledOptional;
  };

  // İSG incelemesi kilidi 2 dakika tazelenmezse düşer; liste 30 sn.de bir yeniden değerlendirir
  const [, setLockTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setLockTick(n => n + 1), 30 * 1000); return () => clearInterval(t); }, []);
  const isReviewActive = (permit) => !!permit?.review_started_at && Date.now() - new Date(permit.review_started_at).getTime() < 2 * 60 * 1000;

  const [qrPermit, setQrPermit] = useState(null);
  const unlockReview = async (permit) => {
    if (!(await confirmDialog({ title: 'İnceleme kilidi kaldırılsın mı?', description: `#${permit.request_number} talebini ${permit.review_started_by || 'İSG uzmanı'} inceliyor görünüyor. Kişi gerçekten ekrandaysa kilit 45 sn içinde tekrar oluşur.`, confirmLabel: 'Kilidi Kaldır' }))) return;
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/work-permits/${permit.id}/review-unlock`);
      toast.success('İnceleme kilidi kaldırıldı');
      fetchPermits();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kilit kaldırılamadı');
    }
  };

  const reviewLockInfo = (permit) => {
    const since = new Date(permit.review_since || permit.review_started_at);
    const mins = Math.max(0, Math.floor((Date.now() - since.getTime()) / 60000));
    const time = since.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    return `${permit.review_started_by || 'İSG uzmanı'} · ${time} itibarıyla (${mins < 1 ? 'az önce' : `${mins} dk`})`;
  };

  // Status badge
  const getStatusBadge = (status, permit = null) => {
    const actualStatus = permit ? getActualStatus(permit) : status;
    switch (actualStatus) {
      case 'aktif': return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Onaylandı</Badge>;
      case 'taslak': return <Badge className="bg-slate-700 text-white hover:bg-slate-700 flex items-center gap-1 font-semibold"><FileText className="h-3 w-3" />Taslak</Badge>;
      case 'eksik_hatali': return isFirm && permit ? (
        <button type="button" onClick={() => handleOpenDialog(permit)} data-testid={`status-eksik-edit-${permit.id}`}>
          <Badge className="bg-rose-600 text-white hover:bg-rose-700 flex items-center gap-1 cursor-pointer"><AlertCircle className="h-3 w-3" />Eksik/Hatalı</Badge>
        </button>
      ) : <Badge className="bg-rose-600 text-white hover:bg-rose-600 flex items-center gap-1"><AlertCircle className="h-3 w-3" />Eksik/Hatalı</Badge>;
      case 'talep_bilgisi_hatali': return isFirm && permit ? (
        <button type="button" onClick={() => handleOpenDialog(permit)} data-testid={`status-info-error-edit-${permit.id}`}>
          <Badge className="bg-fuchsia-600 text-white hover:bg-fuchsia-700 flex items-center gap-1 cursor-pointer whitespace-nowrap"><AlertCircle className="h-3 w-3" />Talep Bilgisi Hatalı</Badge>
        </button>
      ) : <Badge className="bg-fuchsia-600 text-white hover:bg-fuchsia-600 flex items-center gap-1 whitespace-nowrap"><AlertCircle className="h-3 w-3" />Talep Bilgisi Hatalı</Badge>;
      case 'onay_bekliyor': return <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 flex items-center gap-1"><Clock className="h-3 w-3" />Onay Bekliyor</Badge>;
      case 'isg_inceleme': if (isReviewActive(permit)) return (
        <div className="flex flex-col items-center gap-0.5 text-center">
          <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100 flex items-center gap-1.5 whitespace-nowrap" data-testid={`status-isg-checking-${permit.id}`}>
            <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75 animate-ping" /><span className="relative inline-flex h-2 w-2 rounded-full bg-orange-500" /></span>
            <Loader2 className="h-3 w-3 animate-spin" />{isFirm ? 'İSG kontrol ediyor' : 'Kontrol ediliyor'}
          </Badge>
          <span className="text-[11px] leading-tight text-orange-700 whitespace-nowrap" data-testid={`status-isg-checking-info-${permit.id}`}>{reviewLockInfo(permit)}</span>
          {permit.review_progress?.total > 0 && (
            <div className="flex w-full max-w-[180px] items-center gap-1.5" data-testid={`status-isg-progress-${permit.id}`} title={`Tamamlanan: %${Math.round((permit.review_progress.done / permit.review_progress.total) * 98)} (${permit.review_progress.done} / ${permit.review_progress.total} belge incelendi)`}>
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-orange-100">
                <div className={`h-full rounded-full transition-[width] duration-500 ${permit.review_progress.done >= permit.review_progress.total ? 'bg-amber-500' : 'bg-orange-500'}`} style={{ width: `${Math.round((permit.review_progress.done / permit.review_progress.total) * 98)}%` }} />
              </div>
              <span className={`text-[10px] font-semibold tabular-nums text-orange-700`}>%{Math.round((permit.review_progress.done / permit.review_progress.total) * 98)}</span>
            </div>
          )}
          {user?.is_super_admin && (
            <button type="button" onClick={(e) => { e.stopPropagation(); unlockReview(permit); }}
              className="mt-0.5 inline-flex items-center gap-1 rounded border border-orange-300 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-orange-700 transition-colors hover:bg-orange-50" data-testid={`review-unlock-${permit.id}`}>
              <Unlock className="h-3 w-3" />Kilidi Kaldır
            </button>
          )}
        </div>
      );
      return <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100 flex items-center gap-1" data-testid="status-isg-inceleme"><ClipboardCheck className="h-3 w-3" />İSG İncelemesinde</Badge>;
      case 'red_edildi': return <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 flex items-center gap-1"><XCircle className="h-3 w-3" />Reddedildi</Badge>;
      case 'geri_cekildi': return <Badge className="bg-slate-200 text-slate-600 hover:bg-slate-200 flex items-center gap-1"><XCircle className="h-3 w-3" />Geri Çekildi</Badge>;
      case 'suresi_dolmus': return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Süresi Dolmuş</Badge>;
      case 'tamamlandi': return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Tamamlandı</Badge>;
      case 'iptal': return <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">İptal</Badge>;
      default: return <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">{status}</Badge>;
    }
  };

  const getStatusTip = (permit) => {
    const st = getActualStatus(permit);
    if (st === 'isg_inceleme' && isFirm && isReviewActive(permit)) return permit.review_started_by ? `${permit.review_started_by} şu anda talebinizi kontrol ediyor` : 'İSG uzmanı şu anda talebinizi kontrol ediyor';
    const firm = {
      aktif: 'İzniniz onaylandı; belirtilen tarihlerde çalışma yapabilirsiniz',
      taslak: 'Talep henüz gönderilmedi; "Devam Et" ile tamamlayıp gönderin',
      eksik_hatali: 'Eksik/hatalı belgeler var; düzenlemek için tıklayın',
      talep_bilgisi_hatali: 'Temel bilgilerde hata var; önerilen bilgileri girmek için tıklayın',
      isg_inceleme: 'Talebiniz İSG uzmanının incelemesini bekliyor',
      onay_bekliyor: 'Belgeler incelendi; son onay bekleniyor',
      red_edildi: 'Talebiniz reddedildi; gerekirse yeni talep oluşturun',
      geri_cekildi: 'Talep geri çekildi ve işleme kapatıldı',
      suresi_dolmus: 'İzin süresi doldu; çalışma için yeni talep oluşturun',
    };
    const reviewer = {
      aktif: 'Onaylanmış, geçerli çalışma izni',
      taslak: 'Firma talebi henüz tamamlayıp göndermedi',
      eksik_hatali: 'Firmanın eksik/hatalı belgeleri düzeltmesi bekleniyor',
      talep_bilgisi_hatali: 'Firmanın temel bilgileri önerilen şekilde düzeltmesi bekleniyor',
      isg_inceleme: 'Belgelerin incelenmesi gerekiyor; "İncele" ile başlayın',
      onay_bekliyor: 'İnceleme tamamlandı; onaylayın veya reddedin',
      red_edildi: 'Talep reddedildi',
      geri_cekildi: 'Talep geri çekildi',
      suresi_dolmus: 'İzin süresi doldu; artık geçerli değil',
    };
    return (isFirm ? firm : reviewer)[st];
  };

  const getDateTip = (permit) => {
    if (!permit.start_date || !permit.end_date) return null;
    const day = 86400000;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const start = new Date(permit.start_date); start.setHours(0, 0, 0, 0);
    const end = new Date(permit.end_date); end.setHours(0, 0, 0, 0);
    const toStart = Math.round((start - today) / day);
    const toEnd = Math.round((end - today) / day);
    if (toEnd < 0) return { label: `${-toEnd} gün önce sona erdi`, tone: 'danger' };
    if (toStart > 0) return { label: `Başlamasına ${toStart} gün var`, tone: 'dark' };
    if (toEnd === 0) return { label: 'Bugün sona eriyor', tone: 'warn' };
    return { label: `Bitişe ${toEnd} gün kaldı`, tone: toEnd <= 3 ? 'warn' : 'dark' };
  };

  // Onay fonksiyonu
  const handleApprove = async (permitId) => {
    if (!(await confirmDialog({ title: 'Çalışma izni onaylansın mı?', description: 'Bu çalışma iznini onaylamak istediğinizden emin misiniz?', confirmLabel: 'Evet, Onayla', tone: 'primary' }))) return;
    setApproving(true);
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/work-permits/${permitId}/approve`);
      toast.success('Çalışma izni onaylandı');
      // Listeyi yenile
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Onaylama başarısız');
    } finally {
      setApproving(false);
    }
  };

  // Red dialog aç
  const openRejectDialog = (permit) => {
    setRejectingPermit(permit);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  // Red işlemi
  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Red açıklaması zorunludur');
      return;
    }
    setRejecting(true);
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/work-permits/${rejectingPermit.id}/reject`, {
        reason: rejectReason.trim()
      });
      toast.success('Çalışma izni reddedildi');
      setRejectDialogOpen(false);
      // Listeyi yenile
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reddetme başarısız');
    } finally {
      setRejecting(false);
    }
  };

  // İzni yeniden onaya gönder
  const handleResubmit = async (permitId) => {
    if (!(await confirmDialog({ title: 'Tekrar onaya gönderilsin mi?', description: 'Bu izni tekrar onaya göndermek istiyor musunuz?', confirmLabel: 'Evet, Gönder', tone: 'primary' }))) return;
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/work-permits/${permitId}/submit-approval`);
      toast.success('İzin tekrar onaya gönderildi');
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Onaya gönderme başarısız');
    }
  };

  // Sunucudan anlık bildirim gelince (ör. İSG incelemeye başladı) listeyi tek sefer yenile
  useEffect(() => {
    let t = null;
    const onEvent = () => { clearTimeout(t); t = setTimeout(fetchPermits, 400); };
    const onVisible = () => { if (document.visibilityState === 'visible') onEvent(); };
    window.addEventListener('firm-permit-status', onEvent);
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearTimeout(t); window.removeEventListener('firm-permit-status', onEvent); document.removeEventListener('visibilitychange', onVisible); };
  }, [fetchPermits]);

  // Firma: İSG incelemeye başlamadan talebi geri çekip düzenler
  const handleRecall = async (permit) => {
    if (!(await confirmDialog({ title: 'Talep geri çekilsin mi?', description: `${permit.request_number || ''} numaralı talep İSG incelemesinden geri çekilip düzenlemeye açılacak. Düzenledikten sonra tekrar göndermeniz gerekir. Devam edilsin mi?`, confirmLabel: 'Evet, Geri Çek', tone: 'primary' }))) return;
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/work-permits/${permit.id}/recall`);
      toast.success('Talep geri çekildi, düzenleyebilirsiniz');
      navigate(`/work-permits/${permit.id}/edit`);
    } catch (error) {
      if (error.response?.status === 409) { toast.warning(error.response.data.detail, { duration: 6000 }); fetchPermits(); }
      else toast.error(error.response?.data?.detail || 'Talep geri çekilemedi');
    }
  };

  // Geri çekme fonksiyonu
  const handleCopy = async (permit) => {
    if (copyBusy.current.has(permit.id)) return;
    copyBusy.current.add(permit.id);
    const idemName = `wp-copy-idem-${permit.id}`;
    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/work-permits/${permit.id}/copy`, null, idemConfig(storedIdemKey(idemName)));
      clearIdemKey(idemName);
      toast.success(`Talep kopyalandı (Yeni Talep No: ${res.data.request_number}). Lütfen tarih ve saatleri girin.`);
      navigate(`/work-permits/${res.data.id}/edit`);
    } catch (err) {
      if (err.response && err.response.status !== 409) clearIdemKey(idemName);
      if (handleIdemConflict(err, navigate)) return;
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Talep kopyalanamadı');
    } finally {
      copyBusy.current.delete(permit.id);
    }
  };

  const handleWithdraw = async (permit) => {
    const reason = await promptDialog({ title: 'Aktif izin geri çekilsin mi?', description: `${permit.request_number || ''} numaralı izin geri çekilecek ve firmaya bilgi gönderilecek. Geri çekme nedenini yazın (opsiyonel):`, placeholder: 'Geri çekme nedeni', confirmLabel: 'Evet, Geri Çek' });
    if (reason === null) return; // İptal edildi
    
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/work-permits/${permit.id}/withdraw`, { reason });
      toast.success(`Talep ${permit.request_number} geri çekildi. Firmaya bilgi gönderildi.`);
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Geri çekme başarısız');
    }
  };

  // Highlight matching text
  const highlightMatch = (text, query) => {
    if (!query || query.length < 3) return text;
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) => regex.test(part) ? <span key={i} className="text-red-600 font-bold">{part}</span> : part);
  };

  // Excel Export
  const exportToExcel = () => {
    try {
      const rows = filteredPermits.map(permit => {
        const permitEmployees = (permit.employee_ids || []).map(id => getEmployeeById(id)).filter(Boolean);
        const employeeList = permitEmployees.map(emp => `${emp.name} ${emp.surname}${emp.tc_kimlik_no ? ` (${maskTcNo(emp.tc_kimlik_no)})` : ''}`).join('\n');
        const workPermitTypeNames = (permit.work_permit_types || []).map(typeId => WORK_PERMIT_TYPES.find(t => t.id === typeId)?.label).filter(Boolean).join('\n');
        const equipmentNames = (permit.equipment_ids || []).map(id => allEquipment.find(e => e.id === id)?.name).filter(Boolean).join('\n');
        return {
          no: permit.request_number || '-',
          location: getLocationName(permit.location_id),
          company: getCompanyName(permit.company_id),
          job: [permit.is_tipi, permit.is_cesidi].filter(Boolean).join(' / ') || '-',
          desc: permit.job_description || '-',
          employees: employeeList || '-',
          empCount: permitEmployees.length,
          types: workPermitTypeNames || 'Gerekli Değil',
          equipment: equipmentNames || 'Yok',
          start: formatDate(permit.start_date),
          end: formatDate(permit.end_date),
          time: `${permit.start_time || '-'} - ${permit.end_time || '-'}`,
          status: getStatusLabel(getActualStatus(permit)),
        };
      });
      const now = new Date();
      writeStyledExcel({
        fileName: `calisma_izinleri_${now.toISOString().split('T')[0]}.xlsx`,
        sheetName: 'Çalışma İzinleri',
        title: 'ÇALIŞMA İZİNLERİ LİSTESİ',
        subtitle: `${rows.length} kayıt  ·  Oluşturulma: ${now.toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })}`,
        statusKey: 'status',
        columns: [
          { header: 'Talep No', key: 'no', width: 10, bold: true },
          { header: 'Durum', key: 'status', width: 17 },
          { header: 'İşletme', key: 'location', width: 20 },
          { header: 'Firma', key: 'company', width: 22, bold: true },
          { header: 'İş Tipi / Çeşidi', key: 'job', width: 26 },
          { header: 'İş Tanımı', key: 'desc', width: 30, align: 'left' },
          { header: 'Onaylı Personel', key: 'employees', width: 34, align: 'left' },
          { header: 'Kişi', key: 'empCount', width: 7 },
          { header: 'İş İzni', key: 'types', width: 22 },
          { header: 'İş Ekipmanı', key: 'equipment', width: 18 },
          { header: 'Başlangıç', key: 'start', width: 12 },
          { header: 'Bitiş', key: 'end', width: 12 },
          { header: 'Saat', key: 'time', width: 14 },
        ],
        rows,
      });
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error('Excel indirme hatası: ' + error.message);
    }
  };

  // Dialog handlers
  const handleOpenDialog = async (permit = null) => {
    if (permit) {
      // Firma: her girişte eksik bölüm yeniden hesaplanır. İSG: en son kaldığı bölüm açılır (localStorage)
      if (!isReviewer) sessionStorage.removeItem(`wizard-step-${permit.id}`);
      navigate(`/work-permits/${permit.id}/edit`);
    } else {
      // Yeni izin - Tam sayfa wizard'a yönlendir
      navigate('/work-permits/new');
    }
  };

  // Edit wizard kapanınca temizle (artık kullanılmıyor ama bırakalım)
  const handleEditWizardClose = () => {
    setEditWizardOpen(false);
    setEditWizardPermit(null);
  };

  useEffect(() => {
    if (!pendingOpenId) return;
    const p = permits.find(x => x.id === pendingOpenId);
    if (p) { setPendingOpenId(null); handleViewPermit(p); }
  }, [permits, pendingOpenId]);

  const handleViewPermit = async (permit) => {
    // Liste yanıtı hafif; detay için tam izin (belge görüntüsü, işlem geçmişi, eksiklik raporu) alınır
    // Liste alanları aynen korunur; detaydan yalnız listede olmayanlar (doc_snapshot, audit_log vb.) eklenir
    const detail = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/work-permits/${permit.id}`).then(r => r.data).catch(() => ({}));
    const full = { ...detail, ...permit };
    setViewingPermit(full);
    // Ekipman ve personel verilerini güncel olarak çek
    const [emps] = await Promise.all([
      fetchEmployeesForCompany(permit.company_id),
      refreshEquipment(full) // Ekipman verilerini yenile
    ]);
    setEmployees(emps);
    setViewDialogOpen(true);
  };

  // Form handlers
  const handleLocationChange = async (locationId) => {
    setFormData({ ...formData, location_id: locationId, company_id: '', employee_ids: [], equipment_ids: [], work_permit_types: [], is_tipi: '', is_cesidi: '', work_area_id: '' });
    setEmployees([]);
    setCompanyEquipment([]);
    setHasEquipment(false);
    setHasWorkPermitTypes(false);
    setSelectedWorkArea(null);
    setAvailableIsCesitleri([]);
    setEnabledOptionalDocs([]);
    // Work areas'ı yükle
    await fetchWorkAreas(locationId);
  };

  const handleCompanyChange = async (companyId) => {
    setFormData({ ...formData, company_id: companyId, employee_ids: [], equipment_ids: [], work_permit_types: [] });
    const emps = await fetchEmployeesForCompany(companyId);
    setEmployees(emps);
    setCompanyEquipment(getEquipmentForCompany(companyId));
    setHasEquipment(false);
    setHasWorkPermitTypes(false);
  };

  const handleStartDateChange = (value) => {
    const today = getTodayString();
    if (value < today) {
      toast.error('Başlangıç tarihi bugünden önce olamaz!');
      setFormData({ ...formData, start_date: '', end_date: '', employee_ids: [], work_permit_types: [] });
      return;
    }
    if (formData.end_date && value > formData.end_date) {
      toast.warning('Bitiş tarihi başlangıç tarihinden önce olamaz. Bitiş tarihi sıfırlandı.');
      setFormData({ ...formData, start_date: value, end_date: '', employee_ids: [], work_permit_types: [] });
      return;
    }
    setFormData({ ...formData, start_date: value });
  };

  const handleEndDateChange = (value) => {
    const today = getTodayString();
    if (value < today) {
      toast.error('Bitiş tarihi bugünden önce olamaz!');
      setFormData({ ...formData, end_date: '', employee_ids: [], equipment_ids: [], work_permit_types: [] });
      return;
    }
    if (formData.start_date && value < formData.start_date) {
      toast.error('Bitiş tarihi başlangıç tarihinden önce olamaz!');
      setFormData({ ...formData, end_date: '', employee_ids: [], equipment_ids: [], work_permit_types: [] });
      return;
    }
    setFormData({ ...formData, end_date: value, employee_ids: [], equipment_ids: [], work_permit_types: [] });
  };

  const handleEmployeeToggle = (employeeId) => {
    const employee = employees.find(e => e.id === employeeId);
    if (!isEmployeeFullyApproved(employee)) {
      toast.error('Bu personelin belgeleri eksik, seçilemez.');
      return;
    }
    const currentIds = formData.employee_ids || [];
    const newIds = currentIds.includes(employeeId) ? currentIds.filter(id => id !== employeeId) : [...currentIds, employeeId];
    setFormData({ ...formData, employee_ids: newIds });
  };

  const handleEquipmentToggle = (equipmentId) => {
    const equipment = companyEquipment.find(e => e.id === equipmentId);
    const validityInfo = getEquipmentValidityInfo(equipment, formData.end_date);
    if (!validityInfo.isValid) {
      toast.error('Bu ekipmanın periyodik muayenesi geçersiz, seçilemez.');
      return;
    }
    const currentIds = formData.equipment_ids || [];
    const newIds = currentIds.includes(equipmentId) ? currentIds.filter(id => id !== equipmentId) : [...currentIds, equipmentId];
    setFormData({ ...formData, equipment_ids: newIds });
  };

  const handleWorkPermitTypeToggle = (typeId) => {
    const currentTypes = formData.work_permit_types || [];
    const newTypes = currentTypes.includes(typeId) ? currentTypes.filter(id => id !== typeId) : [...currentTypes, typeId];
    setFormData({ ...formData, work_permit_types: newTypes });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const selectedCompany = companies.find(c => c.id === formData.company_id);
    if (selectedCompany && !isCompanyFullyApproved(selectedCompany)) {
      toast.error('Firmanın belgeleri onaylı olmadığı için çalışma izni oluşturamazsınız!');
      return;
    }
    if (formData.employee_ids.length === 0) {
      toast.error('En az bir onaylı personel seçmelisiniz!');
      return;
    }
    try {
      if (editingPermit) {
        await updatePermit(editingPermit.id, formData);
        toast.success('Çalışma izni güncellendi');
      } else {
        await createPermit(formData);
        toast.success('Çalışma izni eklendi');
      }
      setDialogOpen(false);
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirmDialog({ title: 'Çalışma izni silinsin mi?', description: 'Bu çalışma iznini silmek istediğinizden emin misiniz?', confirmLabel: 'Evet, Sil' }))) return;
    try {
      await deletePermit(id);
      toast.success('Çalışma izni silindi');
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  if (!online && !isFirm) return <OfflinePermitsView userId={user?.id} />;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  // Durum sayılarını hesapla
  const pendingCount = permits.filter(p => p.status === 'onay_bekliyor').length;
  const rejectedCount = permits.filter(p => p.status === 'red_edildi').length;
  const reviewCount = permits.filter(p => p.status === 'isg_inceleme' && (isFirm || hasLocationAccess(p.location_id))).length;
  const lowCount = permits.filter(p => isLowNew(p) && hasLocationAccess(p.location_id)).length;

  return (
    <div data-testid="work-permits-page" className="space-y-6">
      <PermitQrDialog permit={qrPermit} companyName={qrPermit ? getCompanyName(qrPermit.company_id) : ""} onClose={() => setQrPermit(null)} />
      {/* Bekleyen/Reddedilen İzin Bildirimi */}
      {(pendingCount > 0 || rejectedCount > 0 || reviewCount > 0 || lowCount > 0) && (
        <div className="flex flex-wrap gap-3">
          {lowCount > 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-rose-300 bg-rose-100 px-4 py-2.5 cursor-pointer transition-colors hover:bg-rose-200" onClick={() => setStatusFilter('dusuk_puan')} data-testid="low-score-banner">
              <AlertTriangle className="h-5 w-5 text-rose-700" />
              <span className="font-medium text-rose-800"><span className="text-lg font-bold">{lowCount}</span> yeni talep Five Star puanı {String(lowScore).replace('.', ',')}'in altındaki firmalardan</span>
            </div>
          )}
          {reviewCount > 0 && (
            <div
              className="flex items-center gap-2 px-4 py-2.5 bg-sky-50 border border-sky-200 rounded-lg cursor-pointer hover:bg-sky-100 transition-colors"
              onClick={() => setStatusFilter('isg_inceleme')}
              data-testid="review-banner"
            >
              <ClipboardCheck className="h-5 w-5 text-sky-600" />
              <span className="font-medium text-sky-700">
                <span className="text-lg font-bold">{reviewCount}</span> {isReviewer ? 'adet talep İSG incelemenizi bekliyor' : 'adet talebiniz İSG incelemesinde'}
              </span>
            </div>
          )}
          {pendingCount > 0 && (
            <div 
              className="flex items-center gap-2 px-4 py-2.5 bg-indigo-50 border border-indigo-200 rounded-lg cursor-pointer hover:bg-indigo-100 transition-colors"
              onClick={() => setStatusFilter('onay_bekliyor')}
              data-testid="pending-banner"
            >
              <Clock className="h-5 w-5 text-indigo-600" />
              <span className="font-medium text-indigo-700">
                <span className="text-lg font-bold">{pendingCount}</span> adet onay bekleyen izin var
              </span>
            </div>
          )}
          {rejectedCount > 0 && (
            <div 
              className="flex items-center gap-2 px-4 py-2.5 bg-rose-50 border border-rose-200 rounded-lg cursor-pointer hover:bg-rose-100 transition-colors"
              onClick={() => setStatusFilter('red_edildi')}
              data-testid="rejected-banner"
            >
              <XCircle className="h-5 w-5 text-rose-600" />
              <span className="font-medium text-rose-700">
                <span className="text-lg font-bold">{rejectedCount}</span> adet reddedilmiş izin var
              </span>
            </div>
          )}
        </div>
      )}

      {/* Header */}
      <KvkkRenewalBanner company={myCompany} onChanged={reloadMyCompany} />
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="flex flex-wrap items-center gap-3 text-2xl font-bold text-slate-900">Çalışma İzinleri
            {!howtoHidden && <>
            <button type="button" onClick={openHowto} className={`relative inline-flex items-center gap-1.5 rounded-full bg-orange-500 px-3.5 py-1.5 text-sm font-semibold text-white shadow-md shadow-orange-200 transition-[background-color,transform] hover:bg-orange-600 active:scale-95 ${howtoPulse ? 'howto-pulse' : ''}`} data-testid="permit-howto-btn">
              <HelpCircle className="h-4 w-4" />Nasıl talep oluşturulur?
            </button>
            <button type="button" onClick={openDefHowto} className={`relative inline-flex items-center gap-1.5 rounded-full bg-rose-600 px-3.5 py-1.5 text-sm font-semibold text-white shadow-md shadow-rose-200 transition-[background-color,transform] hover:bg-rose-700 active:scale-95 ${defPulse ? 'howto-pulse howto-pulse-rose' : ''}`} data-testid="permit-deficient-howto-btn">
              <AlertTriangle className="h-4 w-4" />Eksik/Hatalı Talepler?
            </button>
            <button type="button" onClick={openMaskHowto} className={`relative inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-3.5 py-1.5 text-sm font-semibold text-white shadow-md shadow-slate-300 transition-[background-color,transform] hover:bg-slate-700 active:scale-95 ${maskPulse ? 'howto-pulse' : ''}`} data-testid="permit-mask-howto-btn">
              <EyeOff className="h-4 w-4" />Sağlık raporu nasıl maskelenir?
            </button>
            </>}
            <button type="button" onClick={toggleHowtoHidden} className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-600 transition-[background-color,color,transform] hover:bg-slate-100 hover:text-slate-900 active:scale-95" title={howtoHidden ? 'Yardım düğmelerini göster' : 'Yardım düğmelerini gizle'} data-testid="permit-howto-toggle-btn">
              {howtoHidden ? <><Eye className="h-3.5 w-3.5" />Göster</> : <><EyeOff className="h-3.5 w-3.5" />Gizle</>}
            </button>
          </h1>
          <PermitHowtoDialog open={defOpen} onClose={() => setDefOpen(false)} steps={DEFICIENT_STEPS} sections={DEFICIENT_SECTIONS}
            title="Eksik/Hatalı talep nasıl düzeltilir?" description="İSG'nin eksik bulduğu talebi bulma, eksiği görme, belgeyi yeniden yükleme ve tekrar gönderme adımları."
            buttonLabel="Eksik/Hatalı Talepler?" startLabel="Eksik Taleplerimi Göster" onStart={() => { setDefOpen(false); setStatusFilter('eksik_hatali'); }} />
          <PermitHowtoDialog open={maskOpen} onClose={() => setMaskOpen(false)} steps={MASK_STEPS} sections={MASK_SECTIONS}
            title="Sağlık raporu nasıl maskelenir?" description="KVKK gereği sağlık raporundaki gereksiz sağlık bilgilerinin siyah alanlarla kalıcı olarak kapatılması."
            buttonLabel="Sağlık raporu nasıl maskelenir?" />
          <PermitHowtoDialog open={howtoOpen} onClose={() => setHowtoOpen(false)} onStart={isFirm && !kvkkLocked ? () => { setHowtoOpen(false); navigate('/work-permits/new'); } : null} />
          <p className="text-slate-500 mt-1">Firma çalışma izinlerini yönetin</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {canCalendar && (
            <div className="flex rounded-lg border p-0.5 bg-slate-50" data-testid="permits-view-toggle">
              {[['list', 'Liste', List], ['calendar', 'Takvim', CalendarDays]].map(([k, l, Icon]) => (
                <button key={k} type="button" onClick={() => setPageView(k)} data-testid={`permits-view-${k}`} aria-pressed={(k === 'calendar') === calendarMode}
                  className={`flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-md transition-colors ${(k === 'calendar') === calendarMode ? 'bg-orange-500 font-semibold text-white shadow-sm' : 'text-slate-600 hover:bg-white hover:text-slate-900'}`}><Icon className="h-4 w-4" />{l}</button>
              ))}
            </div>
          )}
          {!isFirm && (
          <Button onClick={exportToExcel} variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800" data-testid="export-excel-btn" disabled={filteredPermits.length === 0}>
            <Download className="h-4 w-4 mr-2" />Excel İndir
          </Button>
          )}
          {isReviewer && (
            <Button onClick={() => setDocRulesOpen(true)} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="doc-rules-btn">
              <BookOpen className="h-4 w-4 mr-2" />Belge Kuralları
            </Button>
          )}
          <DocRulesDialog open={docRulesOpen} onOpenChange={setDocRulesOpen} />
          {isFirm && (
            <Button onClick={() => navigate('/work-permits/new')} disabled={kvkkLocked} title={kvkkLocked ? 'Yeni KVKK taahhütnamesini yükleyene kadar talep oluşturamazsınız' : undefined} className="bg-slate-900 hover:bg-slate-800" data-testid="add-permit-btn">
              <Plus className="h-4 w-4 mr-2" />Çalışma İzni Ekle
            </Button>
          )}
        </div>
      </div>

      {calendarMode && <PermitsCalendar locations={authorizedLocations} companies={companies} onView={handleViewPermit} />}
      {!calendarMode && <>
      {dashFilter && (
        <div className="flex w-fit items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3 py-1.5 text-sm text-orange-800" data-testid="dashboard-filter-chip">
          <span>Dashboard filtresi: <b>{dashFilter.group}</b>{PERIOD_LABELS[dashFilter.period] ? ` · ${PERIOD_LABELS[dashFilter.period]}` : ''}</span>
          <button type="button" onClick={() => setDashFilter(null)} className="rounded-full p-0.5 hover:bg-orange-200" aria-label="Filtreyi temizle" data-testid="dashboard-filter-clear"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}
      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Label>İşletme:</Label>
          <Select value={selectedLocationFilter} onValueChange={setSelectedLocationFilter}>
            <SelectTrigger className="w-48" data-testid="location-filter">
              <SelectValue placeholder="İşletme seçin" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm İşletmeler</SelectItem>
              {authorizedLocations.map((location) => <SelectItem key={location.id} value={location.id}>{location.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label>Durum:</Label>
          <StatusMultiFilter value={statusSel} onChange={saveStatusSel} hideLowScore={isFirm} />
        </div>
        <div className="flex items-center gap-2">
          <Label className="whitespace-nowrap">Talep No:</Label>
          <div className="relative w-36">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="örn: 0007" value={requestNumberSearch} onChange={(e) => setRequestNumberSearch(e.target.value)} className="pl-8" data-testid="request-number-search" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Label className="whitespace-nowrap">Firma Ara:</Label>
          <div className="relative w-44">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="min 3 harf" value={companySearchQuery} onChange={(e) => setCompanySearchQuery(e.target.value)} className="pl-8" data-testid="company-search" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Label className="whitespace-nowrap">Personel Ara:</Label>
          <div className="relative w-56">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Ad, soyad veya T.C." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-8" data-testid="employee-search" />
          </div>
        </div>
      </div>

      {/* Results info */}
      <div className="text-sm text-slate-500">
        {filteredPermits.length} / {permits.length} çalışma izni gösteriliyor
        {requestNumberSearch.length >= 1 && <span className="ml-2 text-blue-600">• Talep No: &ldquo;{requestNumberSearch}&rdquo;</span>}
        {companySearchQuery.length >= 3 && <span className="ml-2 text-red-600">• Firma: &ldquo;{companySearchQuery}&rdquo;</span>}
        {searchQuery.length >= 3 && <span className="ml-2 text-red-600">• Personel: &ldquo;{searchQuery}&rdquo;</span>}
      </div>

      {/* Table */}
      <TooltipProvider>
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <div className="flex flex-wrap gap-2 border-b px-3 py-2.5 md:hidden" data-testid="permits-mobile-name-toggles">
              {[['Ekipman', showAllEquipment, setShowAllEquipment, 'equipment'], ['Personel', showAllEmployees, setShowAllEmployees, 'employee']].map(([l, on, set, k]) => (
                <button key={k} type="button" onClick={() => set(v => !v)} aria-pressed={on} data-testid={`permits-mobile-toggle-${k}-names`}
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${on ? 'border-orange-500 bg-orange-500 text-white' : 'border-slate-200 bg-white text-slate-600'}`}>
                  {on ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}{l} isimleri
                </button>
              ))}
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold text-center">Talep No</TableHead>
                  <TableHead className="font-semibold text-center">Durum</TableHead>
                  <TableHead className="font-semibold text-center">Lokasyon</TableHead>
                  <TableHead className="font-semibold text-center">İş Tipi / İş Çeşidi</TableHead>
                  <TableHead className="font-semibold text-center">Firma</TableHead>
                  <TableHead className="font-semibold text-center">İş İzinleri</TableHead>
                  <TableHead className="font-semibold text-center">
                    <span className="inline-flex items-center gap-1">Ekipman
                      <button type="button" onClick={() => setShowAllEquipment(v => !v)} className={`rounded p-1 transition-colors ${showAllEquipment ? 'bg-emerald-100 text-emerald-700' : 'text-slate-400 hover:bg-slate-200 hover:text-slate-700'}`} title={showAllEquipment ? 'İsimleri gizle' : 'Tüm ekipman isimlerini göster'} data-testid="permits-toggle-equipment-names">
                        {showAllEquipment ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </span>
                  </TableHead>
                  <TableHead className="font-semibold text-center">
                    <span className="inline-flex items-center gap-1">Onaylı Personel
                      <button type="button" onClick={() => setShowAllEmployees(v => !v)} className={`rounded p-1 transition-colors ${showAllEmployees ? 'bg-emerald-100 text-emerald-700' : 'text-slate-400 hover:bg-slate-200 hover:text-slate-700'}`} title={showAllEmployees ? 'İsimleri gizle' : 'Tüm personel isimlerini göster'} data-testid="permits-toggle-employee-names">
                        {showAllEmployees ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </span>
                  </TableHead>
                  <TableHead className="font-semibold text-center">Tarih / Saat</TableHead>
                  <TableHead className="font-semibold text-center">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPermits.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-12">
                      <div className="flex flex-col items-center gap-2">
                        <ClipboardCheck className="h-8 w-8 text-slate-300" />
                        <p className="text-slate-500">{permits.length === 0 ? 'Henüz çalışma izni eklenmemiş' : 'Arama kriterlerine uygun izin bulunamadı'}</p>
                        {permits.length === 0 && <Button variant="outline" size="sm" onClick={() => navigate('/work-permits/new')}>İlk çalışma iznini ekle</Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : paginatedPermits.map((permit) => {
                  const permitEmployees = (permit.employee_ids || []).map(id => getEmployeeById(id)).filter(Boolean);
                  const companyName = getCompanyName(permit.company_id);
                  const isCompanyMatch = companySearchQuery.length >= 3 && companyName.toLowerCase().includes(companySearchQuery.toLowerCase());
                  
                  return (
                    <TableRow key={permit.id} className="table-row-hover">
                      <TableCell className="text-center">
                        <span className="font-mono text-xs bg-slate-100 px-2 py-1 rounded">{permit.request_number || '-'}</span>
                      </TableCell>
                      <TableCell>
                        {permit.is_passive ? (
                          <div className="flex flex-col items-center" data-testid={`permit-passive-${permit.id}`}>
                            <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 flex items-center gap-1 w-fit"><Ban className="h-3 w-3" />Pasife alınmıştır</Badge>
                            {permit.passive_reason && <p className="text-xs text-rose-600 mt-1 max-w-xs truncate" title={permit.passive_reason}>{permit.passive_reason}</p>}
                          </div>
                        ) : isFirm && !permit.is_passive && permit.status === 'taslak' ? (
                          <div className="flex justify-center">
                            <ActionTip label="Taslağa devam etmek için tıklayın" testId={`tip-status-${permit.id}`}>
                              <button type="button" onClick={() => handleOpenDialog(permit)} className="rounded-md transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-slate-400" data-testid={`draft-status-open-${permit.id}`}>
                                {getStatusBadge(permit.status, permit)}
                              </button>
                            </ActionTip>
                          </div>
                        ) : <div className="flex justify-center">{getStatusTip(permit) ? <ActionTip label={getStatusTip(permit)} testId={`tip-status-${permit.id}`}>{getStatusBadge(permit.status, permit)}</ActionTip> : getStatusBadge(permit.status, permit)}</div>}
                        {permit.store_objection && (
                          <div className="mt-1 flex justify-center"><Badge className={`${permit.store_objection.resolution ? "bg-slate-200 text-slate-700 hover:bg-slate-200" : "bg-rose-600 text-white hover:bg-rose-600"} text-[11px] whitespace-nowrap`} title={permit.store_objection.note || 'Mağaza: bilgim dahilinde değil'} data-testid={`permit-store-objection-${permit.id}`}>{permit.store_objection.resolution ? (permit.store_objection.resolution.action === 'continue' ? 'İtiraz: devam' : 'İtiraz: iptal') : 'Mağaza itirazı var'}</Badge></div>
                        )}
                        {!permit.is_passive && permit.status === 'red_edildi' && permit.rejection_reason && (
                          <p className="text-xs text-rose-600 mt-1 max-w-xs truncate" title={permit.rejection_reason}>
                            Red: {permit.rejection_reason}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-center">{getLocationName(permit.location_id)}</TableCell>
                      <TableCell className="text-center">
                        {permit.is_tipi || permit.is_cesidi ? <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100" data-testid={`permit-job-type-${permit.id}`}>{[permit.is_tipi, permit.is_cesidi].filter(Boolean).join(' / ')}</Badge> : <span className="text-slate-400 text-xs">-</span>}
                      </TableCell>
                      <TableCell className={`text-center font-medium ${isCompanyMatch ? 'text-red-600' : ''}`}>
                        {highlightMatch(companyName, companySearchQuery)}
                        {permit.on_behalf_of && <p className="text-xs font-normal text-sky-700" data-testid={`permit-behalf-${permit.id}`}>{permit.on_behalf_of} adına</p>}
                        {!isFirm && starScores[permit.behalf_company_id || permit.company_id] && <div className="mt-0.5 flex justify-center" data-testid={`permit-company-star-${permit.id}`}><StarScore value={starScores[permit.behalf_company_id || permit.company_id].avg} jobs={starScores[permit.behalf_company_id || permit.company_id].jobs} lowData={starScores[permit.behalf_company_id || permit.company_id].low_data} /></div>}
                        {!isFirm && starOf(permit)?.gold && <span className="mt-0.5 ml-1 inline-flex items-center gap-0.5 rounded-full border border-yellow-500 bg-gradient-to-b from-yellow-200 via-yellow-400 to-amber-500 px-1.5 py-0.5 text-[10px] font-bold text-amber-950" data-testid={`permit-gold-${permit.id}`}>★ Altın Firma</span>}
                        {isLow(permit) && !isFirm && <span className="mt-0.5 inline-block rounded bg-rose-600 px-1.5 py-0.5 text-[10px] font-bold text-white" data-testid={`permit-low-score-${permit.id}`}>Düşük puan</span>}
                      </TableCell>
                      <TableCell className="text-center">
                        {permit.work_permit_types?.length > 0 ? (
                          <div className="flex flex-wrap justify-center gap-1">
                            {permit.work_permit_types.slice(0, 2).map(typeId => {
                              const typeInfo = WORK_PERMIT_TYPES.find(t => t.id === typeId);
                              return <Badge key={typeId} variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">{typeInfo?.label || typeId}</Badge>;
                            })}
                            {permit.work_permit_types.length > 2 && <Badge variant="outline" className="text-xs">+{permit.work_permit_types.length - 2}</Badge>}
                          </div>
                        ) : <span className="text-slate-400 text-xs">-</span>}
                      </TableCell>
                      <TableCell className="text-center">
                        {(() => {
                          const eqs = (permit.equipment_ids || []).map(id => allEquipment.find(e => e.id === id)).filter(Boolean);
                          if (!eqs.length) return <span className="text-slate-400 text-xs">-</span>;
                          const icon = (eq) => (eq.equipment_type === 'arac' ? <Truck className="h-3 w-3 mr-1" /> : <Wrench className="h-3 w-3 mr-1" />);
                          if (showAllEquipment) return (
                            <div className="flex flex-wrap justify-center gap-1">
                              {eqs.map(eq => <span key={eq.id} className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-slate-100 text-slate-700">{icon(eq)}{eq.name}</span>)}
                            </div>
                          );
                          return (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex cursor-default items-center gap-1 rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200" data-testid={`permit-equipment-count-${permit.id}`}>
                                  <Truck className="h-3 w-3" />{eqs.length} ekipman
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-h-72 max-w-xs overflow-y-auto bg-slate-800 text-white">
                                <ol className="space-y-0.5 text-xs">
                                  {eqs.map((eq, k) => <li key={eq.id}>{k + 1}. {eq.name} <span className="text-slate-400">({eq.equipment_type === 'arac' ? 'Araç' : 'İş Ekipmanı'})</span></li>)}
                                </ol>
                              </TooltipContent>
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {permitEmployees.length > 0 && !showAllEmployees && searchQuery.length < 3 ? (
                          <div className="flex justify-center">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex cursor-default items-center gap-1 rounded-md bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100" data-testid={`permit-employee-count-${permit.id}`}>
                                  <Users className="h-3 w-3" />{permitEmployees.length} personel
                                  {!isFirm && permitEmployees.some(e => empScores[e.id]?.gold) && <span className="ml-1 rounded-full border border-yellow-500 bg-gradient-to-b from-yellow-200 via-yellow-400 to-amber-500 px-1 text-[9px] font-bold text-amber-950" data-testid={`permit-emp-gold-count-${permit.id}`}>★ {permitEmployees.filter(e => empScores[e.id]?.gold).length} Altın Personel</span>}
                                  {!isFirm && permitEmployees.some(e => empScores[e.id]?.avg < lowScore) && <span className="ml-1 rounded bg-rose-600 px-1 text-[9px] font-bold text-white" data-testid={`permit-emp-low-count-${permit.id}`}>{permitEmployees.filter(e => empScores[e.id]?.avg < lowScore).length} düşük puan</span>}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-h-72 max-w-xs overflow-y-auto bg-slate-800 text-white">
                                <ol className="space-y-0.5 text-xs">
                                  {permitEmployees.map((emp, i) => (
                                    <li key={emp.id} className={emp.is_blocked ? 'text-rose-400 line-through decoration-rose-500' : emp.is_archived || emp.is_active === false ? 'text-slate-400 line-through' : ''} title={emp.is_blocked ? 'Bloklanmıştır' : emp.is_archived ? 'Silinmiştir' : undefined}>{i + 1}. {emp.name} {emp.surname}{emp.is_blocked ? ' (Bloklanmıştır)' : emp.is_archived ? ' (Silinmiştir)' : ''}{permit.employee_tasks?.[emp.id] ? <span className="text-emerald-300"> – {permit.employee_tasks[emp.id]}</span> : null}{!isFirm && empScores[emp.id]?.gold ? <span className="text-yellow-300 font-semibold"> · ★ Altın Personel</span> : null}{!isFirm && empScores[emp.id]?.avg < lowScore ? <span className="text-rose-300 font-semibold"> · Düşük puan {empScores[emp.id].avg}</span> : null}</li>
                                  ))}
                                </ol>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        ) : (
                        <div className="flex flex-wrap justify-center gap-1 max-w-md mx-auto">
                          {permitEmployees.length > 0 ? permitEmployees.map(emp => {
                            const fullName = `${emp.name} ${emp.surname}`;
                            const task = permit.employee_tasks?.[emp.id];
                            const isPassive = emp.is_active === false;
                            const isSearchMatch = searchQuery.length >= 3 && fullName.toLowerCase().includes(searchQuery.toLowerCase());
                            
                            return (
                              <Tooltip key={emp.id}>
                                <TooltipTrigger asChild>
                                  <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs cursor-pointer transition-colors ${
                                    emp.is_blocked
                                      ? 'bg-rose-50 text-rose-600 line-through decoration-2 decoration-rose-600 border border-rose-300'
                                      : emp.is_archived
                                      ? 'bg-slate-100 text-slate-400 line-through'
                                      : isPassive 
                                      ? 'bg-slate-100 text-slate-400 line-through opacity-60' 
                                      : isSearchMatch 
                                        ? 'bg-red-100 text-red-700 border border-red-300' 
                                        : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                                  }`}>
                                    <Users className="h-3 w-3 mr-1" />{highlightMatch(fullName, searchQuery)}
                                    {isPassive && <span className="ml-1 text-[10px]">(Pasif)</span>}
                                    {!isFirm && empScores[emp.id]?.gold && <span className="ml-1 rounded-full border border-yellow-500 bg-gradient-to-b from-yellow-200 via-yellow-400 to-amber-500 px-1 text-[9px] font-bold text-amber-950" data-testid={`permit-emp-gold-${permit.id}-${emp.id}`}>★ Altın Personel</span>}
                                    {!isFirm && empScores[emp.id]?.avg < lowScore && <span className="ml-1 rounded bg-rose-600 px-1 text-[9px] font-bold text-white" data-testid={`permit-emp-low-${permit.id}-${emp.id}`}>Düşük puan</span>}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="bg-slate-800 text-white max-w-xs">
                                  <p className="font-medium">{fullName}</p>
                                  {emp.is_blocked && <p className="text-rose-300 text-xs mt-1 font-semibold">Bloklanmıştır</p>}
                                  {emp.is_archived && <p className="text-slate-300 text-xs mt-1 font-semibold">Silinmiştir</p>}
                                  {isPassive && <p className="text-amber-300 text-xs mt-1">⚠️ Bu personel pasif durumda</p>}
                                  {task && <p className="text-emerald-300 text-xs mt-1">Görev: {task}</p>}
                                  {!isFirm && empScores[emp.id] && <p className="text-xs mt-1 font-semibold" style={{ color: starColors[level(empScores[emp.id].avg)] }}>Five Star: {empScores[emp.id].avg} / 5 ({empScores[emp.id].jobs} iş)</p>}
                                  {emp.tc_kimlik_no && <p className="text-slate-300 text-xs mt-1">T.C.: {emp.tc_kimlik_no}</p>}
                                </TooltipContent>
                              </Tooltip>
                            );
                          }) : <span className="text-slate-400 text-xs">-</span>}
                        </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {(() => { const dates = <div className="text-sm text-center whitespace-nowrap cursor-default" data-testid={`permit-dates-${permit.id}`}><div>{formatDate(permit.start_date)}{permit.start_time && <span className="ml-1 text-slate-600">{permit.start_time}</span>}</div><div className="text-slate-500">{formatDate(permit.end_date)}{permit.end_time && <span className="ml-1">{permit.end_time}</span>}</div></div>; const tip = getDateTip(permit); return tip ? <div className="flex justify-center"><ActionTip label={tip.label} tone={tip.tone} testId={`tip-dates-${permit.id}`}>{dates}</ActionTip></div> : dates; })()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-center gap-2">
                          {/* Metinli butonlar ikonların solunda */}
                          {(
                          <div className={`${isFirm ? 'w-28' : 'empty:hidden'} flex items-center justify-end gap-1`}>{!permit.is_passive && <>
                            {isFirm && permit.status === 'isg_inceleme' && !isReviewActive(permit) && (
                              <ActionTip label="Talebi düzenlemek için geri çek" testId={`tip-recall-${permit.id}`}><Button size="sm" onClick={() => handleRecall(permit)} className="bg-rose-600 hover:bg-rose-700 text-white text-xs" data-testid={`recall-permit-${permit.id}`}>
                                <Undo2 className="h-3.5 w-3.5 mr-1" />Geri Çek
                              </Button></ActionTip>
                            )}
                            {isReviewer && permit.status === 'isg_inceleme' && hasLocationAccess(permit.location_id) && (
                              <ActionTip label="Talebi incelemeye başla" testId={`tip-review-${permit.id}`}><Button variant="outline" size="sm" onClick={() => handleOpenDialog(permit)} className="text-sky-700 border-sky-200 hover:bg-sky-50 text-xs" data-testid={`review-permit-${permit.id}`}>
                                <ClipboardCheck className="h-3.5 w-3.5 mr-1" />İncele
                              </Button></ActionTip>
                            )}
                            {isFirm && permit.status === 'taslak' && (
                              <ActionTip label="Taslağı kaldığınız yerden tamamlayın" testId={`tip-continue-${permit.id}`}><Button size="sm" onClick={() => handleOpenDialog(permit)} className="bg-rose-600 hover:bg-rose-700 text-white text-xs" data-testid={`continue-permit-${permit.id}`}>
                                Devam Et<ArrowRight className="h-3.5 w-3.5 ml-1" />
                              </Button></ActionTip>
                            )}
                            {isFirm && ['eksik_hatali', 'talep_bilgisi_hatali'].includes(permit.status) && (
                              <ActionTip label={permit.status === 'eksik_hatali' ? 'Eksikleri giderip yeniden gönderin' : 'Önerilen bilgileri girip yeniden gönderin'} testId={`tip-fix-${permit.id}`}><Button size="sm" onClick={() => handleOpenDialog(permit)} className="bg-rose-600 hover:bg-rose-700 text-white text-xs" data-testid={`edit-permit-${permit.id}`}>
                                <Pencil className="h-3.5 w-3.5 mr-1" />Düzenle
                              </Button></ActionTip>
                            )}
                          </>}</div>
                          )}
                          {/* İkonlar: sabit sırada ve sabit yerde (göz, kalem, çöp) */}
                          <div className="flex items-center gap-0 shrink-0">
                            {!isFirm && !permit.is_passive && permit.status === 'aktif' && canEdit('work_permits') && (
                              <ActionTip label="Aktif izni geri çek" testId={`tip-withdraw-${permit.id}`}><Button variant="ghost" size="icon" onClick={() => handleWithdraw(permit)} className="h-7 w-7 text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`withdraw-permit-${permit.id}`}>
                                <Undo2 className="h-4 w-4" />
                              </Button></ActionTip>
                            )}
                            {canEdit('work_permits') && !isFirm && !(!permit.is_passive && permit.status === 'aktif') && permit.status !== 'onay_bekliyor' && <span className="w-7 h-7" />}
                            {!permit.is_passive && permit.status === 'onay_bekliyor' && (
                              <>
                                <ActionTip label="Talebi onayla" testId={`tip-approve-${permit.id}`}><Button variant="ghost" size="icon" onClick={() => handleApprove(permit.id)} className="h-7 w-7 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" data-testid={`approve-permit-${permit.id}`} disabled={approving}>
                                  <Check className="h-4 w-4" />
                                </Button></ActionTip>
                                <ActionTip label="Talebi reddet" testId={`tip-reject-${permit.id}`}><Button variant="ghost" size="icon" onClick={() => openRejectDialog(permit)} className="h-7 w-7 text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`reject-permit-${permit.id}`} disabled={rejecting}>
                                  <X className="h-4 w-4" />
                                </Button></ActionTip>
                              </>
                            )}
                            {isFirm && (!permit.is_passive && permit.status === 'aktif'
                              ? <ActionTip label="Talebi kopyala (tarih ve saatleri yeniden girersiniz)" testId={`tip-copy-${permit.id}`}><Button variant="ghost" size="icon" onClick={() => handleCopy(permit)} disabled={kvkkLocked} className="h-7 w-7 text-sky-700 hover:text-sky-800 hover:bg-sky-50" data-testid={`copy-permit-${permit.id}`}><Copy className="h-4 w-4" /></Button></ActionTip>
                              : <span className="w-7 h-7" />)}
                            {isFirm && (getActualStatus(permit) === 'aktif' && !permit.is_passive
                              ? <ActionTip label="Onay QR kodu (güvenlik girişi için kaydet / gönder)" testId={`tip-qr-${permit.id}`}><Button variant="ghost" size="icon" onClick={() => setQrPermit(permit)} className="h-7 w-7 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50" data-testid={`qr-permit-${permit.id}`}><QrCode className="h-4 w-4" /></Button></ActionTip>
                              : <span className="w-7 h-7" />)}
                            <ActionTip label="Detayları görüntüle" testId={`tip-view-${permit.id}`}><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleViewPermit(permit)} data-testid={`view-permit-${permit.id}`}><Eye className="h-5 w-5" /></Button></ActionTip>
                            {!permit.is_passive && canDeletePermit(permit)
                              ? <ActionTip label="Talebi sil" testId={`tip-delete-${permit.id}`}><Button variant="ghost" size="icon" onClick={() => handleDelete(permit.id)} className="h-7 w-7 text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`delete-permit-${permit.id}`}><Trash2 className="h-4 w-4" /></Button></ActionTip>
                              : isFirm && <span className="w-7 h-7" />}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            
            {/* Pagination */}
            <TablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </CardContent>
        </Card>
      </TooltipProvider>
      </>}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPermit ? 'Çalışma İzni Düzenle' : 'Yeni Çalışma İzni'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Lokasyon</Label>
                  <Select value={formData.location_id} onValueChange={handleLocationChange} required>
                    <SelectTrigger data-testid="permit-location-select"><SelectValue placeholder="Lokasyon seçin" /></SelectTrigger>
                    <SelectContent>
                      {authorizedLocations.map((location) => <SelectItem key={location.id} value={location.id}>{location.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Firma</Label>
                  <Select value={formData.company_id} onValueChange={handleCompanyChange} required disabled={!formData.location_id}>
                    <SelectTrigger data-testid="permit-company-select"><SelectValue placeholder={formData.location_id ? "Firma seçin" : "Önce lokasyon seçin"} /></SelectTrigger>
                    <SelectContent>
                      {getCompaniesForLocation(formData.location_id).map((company) => {
                        const isApproved = isCompanyFullyApproved(company);
                        return (
                          <SelectItem key={company.id} value={company.id} className={isApproved ? 'font-semibold text-slate-900' : 'text-slate-400'}>
                            <div className="flex items-center gap-2">
                              {isApproved ? <CheckCircle className="h-4 w-4 text-emerald-500" /> : <AlertCircle className="h-4 w-4 text-amber-400" />}{company.name}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {formData.company_id && (() => {
                    const selectedCompany = companies.find(c => c.id === formData.company_id);
                    if (selectedCompany && !isCompanyFullyApproved(selectedCompany)) {
                      return (
                        <div className="flex items-start gap-2 p-3 bg-rose-50 border border-rose-200 rounded-lg mt-2">
                          <AlertCircle className="h-5 w-5 text-rose-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-rose-700">Firma belgeleri onaylı değil!</p>
                            <p className="text-xs text-rose-600 mt-1">Bu firma için çalışma izni oluşturamazsınız. Önce Firmalar sayfasından firma belgelerini onaylayın.</p>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>

              {/* İş Tipi ve İş Çeşidi Seçimi */}
              {formData.location_id && availableIsTipleri.length > 0 && (
                <div className="grid grid-cols-2 gap-4 p-4 bg-blue-50/50 rounded-lg border border-blue-100">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-blue-600" />
                      İş Tipi
                    </Label>
                    <Select value={formData.is_tipi} onValueChange={handleIsTipiChange}>
                      <SelectTrigger data-testid="permit-is-tipi-select">
                        <SelectValue placeholder="İş tipi seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableIsTipleri.map((isTipi) => (
                          <SelectItem key={isTipi} value={isTipi}>{isTipi}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-purple-600" />
                      İş Çeşidi
                    </Label>
                    <Select value={formData.is_cesidi} onValueChange={handleIsCesidiChange} disabled={!formData.is_tipi}>
                      <SelectTrigger data-testid="permit-is-cesidi-select">
                        <SelectValue placeholder={formData.is_tipi ? "İş çeşidi seçin" : "Önce iş tipi seçin"} />
                      </SelectTrigger>
                      <SelectContent>
                        {availableIsCesitleri.map((item) => (
                          <SelectItem key={item.id} value={item.is_cesidi}>{item.is_cesidi}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {selectedWorkArea && (
                    <div className="col-span-2 space-y-3">
                      {/* Zorunlu Belgeler Bilgisi */}
                      <div className="p-3 bg-white rounded-lg border border-emerald-200">
                        <p className="text-sm font-medium text-emerald-800 mb-2">Zorunlu Belgeler:</p>
                        <div className="flex flex-wrap gap-2">
                          {(selectedWorkArea.required_company_docs || []).length > 0 && (
                            <div className="text-xs">
                              <span className="text-slate-500">Firma:</span>
                              {(selectedWorkArea.required_company_docs || []).map(doc => (
                                <Badge key={doc} className="ml-1 bg-emerald-100 text-emerald-700 text-xs border border-emerald-300">{doc}</Badge>
                              ))}
                            </div>
                          )}
                          {(selectedWorkArea.required_employee_docs || []).length > 0 && (
                            <div className="text-xs ml-2">
                              <span className="text-slate-500">Personel:</span>
                              {(selectedWorkArea.required_employee_docs || []).map(doc => (
                                <Badge key={doc} className="ml-1 bg-amber-100 text-amber-700 text-xs border border-amber-300">{doc}</Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Opsiyonel Belgeler - Zorunlu Yapılabilir */}
                      {((selectedWorkArea.optional_employee_docs || []).length > 0 || (selectedWorkArea.optional_company_docs || []).length > 0) && (
                        <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                          <p className="text-sm font-medium text-slate-700 mb-2">Opsiyonel Belgeler (zorunlu yapmak için seçin):</p>
                          <div className="space-y-2">
                            {(selectedWorkArea.optional_employee_docs || []).map(doc => (
                              <div key={doc} className="flex items-center gap-2">
                                <Checkbox
                                  id={`opt-${doc}`}
                                  checked={enabledOptionalDocs.includes(doc)}
                                  onCheckedChange={() => toggleOptionalDoc(doc)}
                                />
                                <Label htmlFor={`opt-${doc}`} className="font-normal cursor-pointer text-sm text-slate-600">
                                  {doc}
                                  {enabledOptionalDocs.includes(doc) && (
                                    <Badge className="ml-2 bg-amber-100 text-amber-700 text-xs">Zorunlu yapıldı</Badge>
                                  )}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label>Yapılacak İş</Label>
                <Textarea value={formData.job_description} onChange={(e) => setFormData({ ...formData, job_description: e.target.value })} placeholder="İş tanımını girin" required data-testid="permit-job-description" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Başlangıç Tarihi</Label>
                  <Input type="date" value={formData.start_date} onChange={(e) => handleStartDateChange(e.target.value)} min={getTodayString()} required data-testid="permit-start-date" />
                  <p className="text-xs text-slate-400">Bugün veya sonrası seçilebilir</p>
                </div>
                <div className="space-y-2">
                  <Label>Bitiş Tarihi</Label>
                  <Input type="date" value={formData.end_date} onChange={(e) => handleEndDateChange(e.target.value)} min={formData.start_date || getTodayString()} required data-testid="permit-end-date" />
                  <p className="text-xs text-slate-400">Başlangıç tarihi veya sonrası seçilebilir</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Başlangıç Saati</Label>
                  <Input type="time" value={formData.start_time} onChange={(e) => setFormData({ ...formData, start_time: e.target.value })} required data-testid="permit-start-time" />
                </div>
                <div className="space-y-2">
                  <Label>Bitiş Saati</Label>
                  <Input type="time" value={formData.end_time} onChange={(e) => setFormData({ ...formData, end_time: e.target.value })} required data-testid="permit-end-time" />
                </div>
              </div>

              {/* Personel Seçici - Work Area filtrelemesi ile */}
              {formData.company_id && employees.length > 0 && formData.end_date && (
                <div className="space-y-2">
                  <Label>Çalışacak Personel</Label>
                  {selectedWorkArea ? (
                    <div className="border rounded-lg p-4 max-h-60 overflow-y-auto space-y-2">
                      {(() => {
                        const compliantEmployees = employees.filter(emp => 
                          emp.is_active !== false && isEmployeeCompliantForArea(emp, selectedWorkArea)
                        );
                        const nonCompliantEmployees = employees.filter(emp => 
                          emp.is_active !== false && !isEmployeeCompliantForArea(emp, selectedWorkArea)
                        );
                        
                        return (
                          <>
                            {compliantEmployees.length > 0 ? (
                              <div className="space-y-2">
                                <p className="text-xs font-medium text-emerald-600">Uygun Personel ({compliantEmployees.length})</p>
                                {compliantEmployees.map(emp => (
                                  <div key={emp.id} className="flex items-center gap-2">
                                    <Checkbox
                                      id={emp.id}
                                      checked={formData.employee_ids?.includes(emp.id)}
                                      onCheckedChange={() => handleEmployeeToggle(emp.id)}
                                    />
                                    <Label htmlFor={emp.id} className="font-normal cursor-pointer text-slate-900">
                                      {emp.name} {emp.surname}
                                      {emp.tc_kimlik_no && <span className="text-slate-500 ml-1 font-mono text-xs">({emp.tc_kimlik_no})</span>}
                                    </Label>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-amber-600 text-sm">Bu alan için belgeleri uygun personel bulunmuyor.</p>
                            )}
                            
                            {nonCompliantEmployees.length > 0 && (
                              <div className="mt-4 pt-4 border-t space-y-2">
                                <p className="text-xs font-medium text-slate-400">Belgeleri Eksik Personel ({nonCompliantEmployees.length})</p>
                                {nonCompliantEmployees.map(emp => (
                                  <div key={emp.id} className="flex items-center gap-2 text-slate-400">
                                    <span className="w-4 h-4 flex items-center justify-center text-xs">•</span>
                                    <span>{emp.name} {emp.surname}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  ) : (
                    <EmployeeSelector employees={employees} selectedIds={formData.employee_ids} onToggle={handleEmployeeToggle} permitEndDate={formData.end_date} />
                  )}
                </div>
              )}
              
              {formData.company_id && employees.length === 0 && (
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                  <p className="text-sm text-slate-500">Bu firmaya kayıtlı personel bulunmamaktadır.</p>
                </div>
              )}

              {/* Ekipman Seçici */}
              {formData.company_id && formData.end_date && (
                <EquipmentSelector 
                  equipment={companyEquipment} 
                  selectedIds={formData.equipment_ids} 
                  onToggle={handleEquipmentToggle} 
                  permitEndDate={formData.end_date}
                  hasEquipment={hasEquipment}
                  onHasEquipmentChange={(checked) => {
                    setHasEquipment(checked);
                    if (!checked) setFormData({ ...formData, equipment_ids: [] });
                  }}
                />
              )}

              {/* İş İzni Türleri */}
              {formData.company_id && formData.end_date && (
                <div className="space-y-3 pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2"><ClipboardCheck className="h-4 w-4 text-slate-500" />İş İzni Var mı?</Label>
                    <Switch checked={hasWorkPermitTypes} onCheckedChange={(checked) => { setHasWorkPermitTypes(checked); if (!checked) setFormData({ ...formData, work_permit_types: [] }); }} data-testid="has-work-permit-types-switch" />
                  </div>
                  {hasWorkPermitTypes && (
                    <div className="border rounded-lg p-4 space-y-2">
                      <p className="text-xs font-medium text-slate-600 mb-3">İş İzni Türlerini Seçin:</p>
                      {WORK_PERMIT_TYPES.map(type => (
                        <div key={type.id} className="flex items-center gap-2">
                          <Checkbox id={`wpt-${type.id}`} checked={formData.work_permit_types?.includes(type.id)} onCheckedChange={() => handleWorkPermitTypeToggle(type.id)} />
                          <Label htmlFor={`wpt-${type.id}`} className="font-normal cursor-pointer text-slate-700">{type.label}</Label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>İptal</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-permit-btn">{editingPermit ? 'Güncelle' : 'Ekle'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog - Refactored Component */}
      <WorkPermitDetailDialog
        open={viewDialogOpen}
        onOpenChange={setViewDialogOpen}
        permit={viewingPermit}
        getLocationName={getLocationName}
        getCompanyName={getCompanyName}
        allEmployees={allEmployees}
        allEquipment={allEquipment}
        company={companies.find(c => c.id === viewingPermit?.company_id)}
        onChanged={fetchPermits}
        starScore={viewingPermit && !isFirm ? starOf(viewingPermit) : null}
        lowScore={lowScore}
        empScores={isFirm ? null : empScores}
      />

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <XCircle className="h-5 w-5" />
              Çalışma İznini Reddet
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Bu çalışma iznini reddetmek üzeresiniz. Lütfen red nedenini açıklayın.
              Bu açıklama firmaya e-posta ile gönderilecektir.
            </p>
            <div className="space-y-2">
              <Label htmlFor="reject-reason">Red Açıklaması *</Label>
              <Textarea
                id="reject-reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Reddetme nedenini açıklayın..."
                className="min-h-[100px]"
                data-testid="reject-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setRejectDialogOpen(false)}>İptal</Button>
            <Button 
              type="button" 
              className="bg-rose-600 hover:bg-rose-700" 
              onClick={handleReject}
              disabled={rejecting || !rejectReason.trim()}
              data-testid="confirm-reject-btn"
            >
              {rejecting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Reddediliyor...</> : 'Reddet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkPermitsPage;
